/**
 * Shrink camera photos before upload so a weak site signal still sends them.
 * Falls back to the original file if the browser cannot decode the image.
 */

export const shouldCompressImage = (file, maxBytes = 900 * 1024) => (
  Boolean(file)
  && String(file.type || '').startsWith('image/')
  && (file.size || 0) > maxBytes
);

export const compressImageFile = (file, {
  maxEdge = 1600,
  quality = 0.82,
  maxBytes = 900 * 1024,
} = {}) => {
  if (!file || typeof document === 'undefined') return Promise.resolve(file);
  if (!String(file.type || '').startsWith('image/')) return Promise.resolve(file);
  if ((file.size || 0) <= maxBytes) return Promise.resolve(file);

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxEdge / Math.max(img.width || 1, img.height || 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round((img.width || 1) * scale));
      canvas.height = Math.max(1, Math.round((img.height || 1) * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(file);
          return;
        }
        const name = String(file.name || 'photo.jpg').replace(/\.\w+$/, '.jpg');
        resolve(new File([blob], name, { type: 'image/jpeg' }));
      }, 'image/jpeg', quality);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
};
